<template>
  <div v-if="isAuth" class="profile-card">
    <Menu
      :model="menuItems"
      :popup="true"
      ref="menu"
      appendTo="#ui-popup-root"
      class="profile-menu"
    >
      <template #item="{ item, props }">
        <a
          v-ripple
          class="profile-menu__item"
          :class="{ 'profile-menu__item--logout': item.action === 'logout' }"
          v-bind="props.action"
          @click="handleMenuClick(item)"
        >
          <span :class="['profile-menu__icon', item.icon]" aria-hidden="true" />
          <span class="profile-menu__label">{{ item.name }}</span>
        </a>
      </template>
    </Menu>

    <div class="profile-activator">
      <span class="profile-username">{{ userStore.user?.lastName }} {{ userStore.user?.username }}</span>
      <Button
        icon="pi pi-ellipsis-v"
        class="p-button-text p-button-plain profile-button"
        @click="toggleMenu"
        :aria-label="t('platform.userMenuAria', { username: userStore.user?.username })"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import Menu from 'primevue/menu'
import Button from 'primevue/button'
import { profileSections } from '@/common/constants/menu'
import { useI18n } from '@/common/composables/useI18n'
import type { SupportedLocale } from '@/common/i18n'

const router = useRouter()
const userStore = useUserStore()
const { t, setLocale } = useI18n()
const menu = ref()

const isAuth = computed(() => userStore.isAuthenticated)

const menuItems = computed(() =>
  profileSections.map((item) => ({
    ...item,
    name: item.nameKey ? t(item.nameKey) : item.name,
  })),
)

const toggleMenu = (event: Event) => {
  menu.value.toggle(event)
}

const handleMenuClick = async (item: {
  to?: string
  action?: string
  locale?: SupportedLocale
}) => {
  if (item.to) {
    router.push({ name: item.to })
  } else if (item.action === 'setLocale' && item.locale) {
    setLocale(item.locale)
  } else if (item.action === 'logout') {
    await userStore.signOut()
    router.push('/signin')
  }
}
</script>

<style scoped lang="scss">
.profile-card {
  display: flex;
  align-items: center;
}

.profile-activator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 1.15rem;
}

.profile-username {
  font-weight: 500;
  white-space: nowrap;
}

.profile-button {
  color: rgba(255, 255, 255, 0.9) !important;
  width: 40px;
  height: 40px;

  &:hover {
    background: rgba(255, 255, 255, 0.15) !important;
  }

  &:deep(.p-button-icon) {
    font-size: 1.2rem;
  }
}

@media (max-width: 768px) {
  .profile-username {
    display: none;
  }

  .profile-activator {
    gap: 4px;
  }
}
</style>

<!-- Menu in #ui-popup-root — unscoped panel styles -->
<style lang="scss">
.profile-menu.p-menu {
  --p-menu-item-focus-background: transparent;
  --p-menu-item-active-background: transparent;
  --p-menu-item-background: transparent;
  --p-menu-background: #ffffff;
  --p-menu-item-color: #334155;
  --p-menu-item-focus-color: #0f172a;
  --p-menu-item-icon-color: #64748b;
  --p-menu-item-icon-focus-color: #475569;
  min-width: 11.5rem;
  padding: 0.35rem;
  border: 1px solid var(--app-surface-border, #e8e8ec);
  border-radius: var(--app-radius-card, 10px);
  background: #ffffff;
  box-shadow:
    0 8px 24px rgba(15, 23, 42, 0.12),
    0 2px 6px rgba(15, 23, 42, 0.06);
}

.profile-menu .p-menu-list,
.profile-menu .p-menuitem,
.profile-menu .p-menuitem-content,
.profile-menu .p-menuitem.p-focus > .p-menuitem-content,
.profile-menu .p-menuitem-content.p-focus,
.profile-menu .p-menuitem-content[data-p-focused='true'],
.profile-menu .p-menuitem-content[data-p-highlight='true'] {
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  color: inherit !important;
}

.profile-menu__item.p-menuitem-link,
.profile-menu__item {
  display: flex !important;
  align-items: center !important;
  gap: 0.625rem !important;
  width: 100% !important;
  min-height: 2.5rem !important;
  padding: 0.5rem 0.75rem !important;
  border-radius: var(--app-radius-control, 8px) !important;
  border: 1px solid transparent !important;
  background: transparent !important;
  background-image: none !important;
  color: #334155 !important;
  text-decoration: none !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  line-height: 1.2 !important;
  cursor: pointer;
  box-sizing: border-box;
  box-shadow: none !important;
  transition:
    background-color 0.12s ease,
    border-color 0.12s ease,
    color 0.12s ease;
}

.profile-menu__icon {
  flex-shrink: 0;
  font-size: 0.95rem;
  color: #64748b;
}

.profile-menu__label {
  flex: 1;
  min-width: 0;
}

.profile-menu__item:hover,
.profile-menu__item:focus,
.profile-menu__item:focus-visible {
  background: #f1f5f9 !important;
  color: #0f172a !important;
  outline: none;
}

.profile-menu__item:hover .profile-menu__icon,
.profile-menu__item:focus .profile-menu__icon,
.profile-menu__item:focus-visible .profile-menu__icon {
  color: #475569 !important;
}

.profile-menu__item--logout {
  color: #9a3412 !important;
}

.profile-menu__item--logout .profile-menu__icon {
  color: #c2410c !important;
}

.profile-menu__item--logout:hover,
.profile-menu__item--logout:focus,
.profile-menu__item--logout:focus-visible {
  background: #fff7ed !important;
  border-color: #fed7aa !important;
  color: #9a3412 !important;
}

.profile-menu__item--logout:hover .profile-menu__icon,
.profile-menu__item--logout:focus .profile-menu__icon,
.profile-menu__item--logout:focus-visible .profile-menu__icon {
  color: #c2410c !important;
}
</style>
